import React, { useState, useEffect } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { StatusBadge } from 'src/components/common/StatusBadge';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  User,
  Loader2,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Send,
  Calculator,
  AlertCircle,
  ChevronDown,
  Mail,
  Phone,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useCandidateScores } from 'src/services/api/queries/candidateQueries';
import {
  useUpdateScore,
  useSendTestLink,
  useCompleteCandidateScore,
} from 'src/services/api/mutations/candidateMutations';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';

const getPassedColor = (passed) => {
  if (passed == null) return 'var(--foreground-strong)';
  return passed ? '#0D9668' : '#DC2626';
};

const getPassedLabel = (passed) => {
  if (passed == null) return '—';
  return passed ? 'Passed' : 'Failed';
};

const UpdateScoreForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const candidateId = searchParams.get('candidateId');
  const opportunityId = searchParams.get('opportunityId');
  const requirementLocationId = searchParams.get('requirementLocationId');
  const assignmentId = searchParams.get('assignmentId');
  const status = searchParams.get('status');
  const firstName = searchParams.get('firstName') || '';
  const lastName = searchParams.get('lastName') || '';
  const email = searchParams.get('email') || '';
  const isReadonly = searchParams.get('readonly') === 'true';
  const phone = searchParams.get('phone') || '';

  const [testScores, setTestScores] = useState({});
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [selectedGroupForResend, setSelectedGroupForResend] = useState(null);
  const [sendingGroupId, setSendingGroupId] = useState(null);

  const {
    data: scoresData,
    isLoading: isScoresLoading,
    error: scoresError,
    refetch: refetchScores,
  } = useCandidateScores(candidateId, opportunityId);

  const updateScoreMutation = useUpdateScore();
  const sendTestLinkMutation = useSendTestLink();
  const completeCandidateScoreMutation = useCompleteCandidateScore();

  useEffect(() => {
    if (scoresData?.success && scoresData.data.items?.groups) {
      const { groups } = scoresData.data.items;
      const scores = {};
      groups.forEach((group) => {
        group.tests.forEach((test) => {
          const key = `${group.id}-${test.id}`;
          scores[key] =
            test.test_score !== null && test.test_score !== undefined
              ? test.test_score.toString()
              : '';
        });
      });
      setTestScores(scores);
    }
  }, [scoresData]);

  useEffect(() => {
    if (scoresError) {
      toast({
        title: 'Error',
        description: scoresError.message || 'Failed to fetch scores',
        variant: 'destructive',
      });
    }
  }, [scoresError, toast]);

  const toggleGroup = (id) => {
    setExpandedGroups((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
  };

  const handleTestScoreChange = (groupId, testId, value) => {
    setTestScores((prev) => ({ ...prev, [`${groupId}-${testId}`]: value }));
  };

  const handleBulkScoreSubmit = async (group) => {
    try {
      const scores = group.tests
        .map((test) => {
          const key = `${group.id}-${test.id}`;
          const score = testScores[key];
          if (score && score.trim() !== '') {
            return { test_id: test.id, score: parseFloat(score) };
          }
          return null;
        })
        .filter(Boolean);

      if (scores.length === 0) {
        toast({
          title: 'No Scores to Submit',
          description: 'Please enter at least one test score before submitting',
          variant: 'destructive',
        });
        return;
      }

      const testCollectionId = scoresData?.data?.items?.test_collection_id;
      await updateScoreMutation.mutateAsync({
        candidate_reference_id: candidateId,
        requirement_id: parseInt(opportunityId, 10),
        order_number: group.order_number,
        group_id: group.id,
        email,
        test_collection_id: testCollectionId,
        scores,
      });

      toast({
        title: 'Success',
        description: `${scores.length} score(s) updated successfully`,
      });
      refetchScores();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update scores',
        variant: 'destructive',
      });
    }
  };

  const handleGoBack = () => {
    if (isReadonly) {
      const p = new URLSearchParams();
      if (opportunityId) p.set('opportunityId', opportunityId);
      if (requirementLocationId)
        p.set('requirementLocationId', requirementLocationId);
      if (assignmentId) p.set('assignmentId', assignmentId);
      if (status) p.set('status', status);
      navigate(`/candidates/list?${p.toString()}`);
    } else {
      navigate(-1);
    }
  };

  const handleSendTestLink = async (groupId, isResend = false) => {
    try {
      setSendingGroupId(groupId);
      const testCollectionId = scoresData?.data?.items?.test_collection_id;
      const group = scoresData?.data?.items?.groups?.find(
        (g) => g.id === groupId
      );
      if (!group) {
        toast({
          title: 'Error',
          description: 'Group information not found',
          variant: 'destructive',
        });
        setSendingGroupId(null);
        return;
      }
      await sendTestLinkMutation.mutateAsync({
        candidate_reference_id: candidateId,
        requirement_id: parseInt(opportunityId, 10),
        test_collection_id: testCollectionId,
        group_id: groupId,
        order_number: group.order_number,
      });
      toast({
        title: isResend ? 'Test Link Resent' : 'Test Link Sent',
        description: `Test link has been ${isResend ? 'resent' : 'sent'} successfully`,
      });
      if (isResend) {
        setResendDialogOpen(false);
        setSelectedGroupForResend(null);
      }
      refetchScores();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to send test link',
        variant: 'destructive',
      });
    } finally {
      setSendingGroupId(null);
    }
  };

  const handleOpenResendDialog = (group) => {
    setSelectedGroupForResend(group);
    setResendDialogOpen(true);
  };

  const handleCloseResendDialog = () => {
    setResendDialogOpen(false);
    setSelectedGroupForResend(null);
  };

  const handleCalculateScore = async () => {
    try {
      if (!requirementLocationId) {
        toast({
          title: 'Error',
          description: 'Requirement location ID is missing',
          variant: 'destructive',
        });
        return;
      }
      await completeCandidateScoreMutation.mutateAsync({
        candidateId,
        opportunityId,
        requirementLocationId,
      });
      toast({ title: 'Success', description: 'Score calculated successfully' });
      refetchScores();
      let url = `/candidates/update-score?candidateId=${candidateId}&opportunityId=${opportunityId}&requirementLocationId=${requirementLocationId}&firstName=${firstName}&lastName=${lastName}&email=${email}&phone=${phone}&readonly=true`;
      if (assignmentId) url += `&assignmentId=${assignmentId}`;
      if (status) url += `&status=${status}`;
      navigate(url);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to calculate score',
        variant: 'destructive',
      });
    }
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const getStatusBadge = (statusValue) => (
    <StatusBadge value={statusValue || 'NOT_STARTED'} />
  );

  const renderTestRow = (group, test, index) => {
    const key = `${group.id}-${test.id}`;
    const currentScore = testScores[key] || '';
    const hasExistingScore =
      test.test_score !== null && test.test_score !== undefined;
    const isPending = group.status === 'PENDING';
    const canEdit =
      !isReadonly && isPending && (group.manual_update || group.manual_entry);
    const isLast = index === group.tests.length - 1;

    return (
      <div
        key={test.id}
        className={`grid gap-3 px-5 py-[13px] items-center${!isLast ? ' border-b border-border' : ''}`}
        style={{ gridTemplateColumns: '1fr 120px 120px' }}
      >
        <div className="!text-left">
          <div className="text-[13px] font-medium text-foreground-strong">
            {test.name}
          </div>
          {test.description && (
            <div className="text-[11.5px] text-muted-foreground mt-[2px] leading-[1.4]">
              {test.description}
            </div>
          )}
        </div>
        <div>
          <span
            className="float-right inline-flex px-[10px] py-[3px] rounded-[5px] text-[12.5px] font-medium"
            style={{ background: '#F3F4F6', color: 'var(--muted-foreground)' }}
          >
            {test.max_score}
          </span>
        </div>
        <div className="flex justify-center">
          {canEdit ? (
            <Input
              type="number"
              value={currentScore}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '' || inputValue === '-') {
                  handleTestScoreChange(group.id, test.id, inputValue);
                  return;
                }
                const numValue = parseFloat(inputValue);
                if (!Number.isNaN(numValue)) {
                  if (numValue > test.max_score) {
                    handleTestScoreChange(
                      group.id,
                      test.id,
                      test.max_score.toString()
                    );
                    return;
                  }
                  if (numValue < 0) {
                    handleTestScoreChange(group.id, test.id, '0');
                    return;
                  }
                }
                handleTestScoreChange(group.id, test.id, inputValue);
              }}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="–"
              className="h-8 text-[13px] w-[90px] text-center"
              step="0.01"
              min="0"
              max={test.max_score}
            />
          ) : (
            <span
              className={`text-[13px] ${hasExistingScore ? 'text-foreground-strong' : 'text-muted-foreground'}`}
              style={{ fontWeight: 550 }}
            >
              {hasExistingScore ? test.test_score : '–'}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--muted)' }}>
      <PageHeader
        title={isReadonly ? 'View Score' : 'Update Score'}
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={handleGoBack}
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
            Back to Candidates
          </Button>
        }
      />

      <div
        className="px-8 py-7 pb-20"
        style={{ maxWidth: '1000px', margin: '0 auto' }}
      >
        {/* Candidate Info Card */}
        <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] p-[22px] mb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className="w-9 h-9 min-w-[36px] rounded-[9px] flex items-center justify-center flex-shrink-0"
              style={{
                background: 'var(--brand-bg-subtle)',
                color: 'var(--primary)',
              }}
            >
              <User className="w-[18px] h-[18px]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="text-base font-bold text-foreground-strong tracking-[-0.02em] mb-[5px]">
                {firstName} {lastName}
              </div>
              <div className="flex flex-col gap-[3px]">
                <div className="flex items-center gap-[5px] text-xs text-muted-foreground">
                  <Mail
                    className="w-3 h-3 text-muted-foreground"
                    strokeWidth={1.8}
                  />
                  {email}
                </div>
                <div className="flex items-center gap-[5px] text-xs text-muted-foreground">
                  <Phone
                    className="w-3 h-3 text-muted-foreground"
                    strokeWidth={1.8}
                  />
                  {phone}
                </div>
              </div>
            </div>
          </div>

          {!isReadonly && (
            <button
              type="button"
              onClick={handleCalculateScore}
              disabled={
                !scoresData?.data?.items?.assessment_complete ||
                completeCandidateScoreMutation.isPending
              }
              className="inline-flex items-center gap-1.5 px-[18px] py-[9px] rounded-lg border-0 text-[13px] font-semibold text-white cursor-pointer transition-all duration-150 disabled:opacity-55 disabled:cursor-not-allowed flex-shrink-0"
              style={{
                background: '#0D9668',
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.background = '#0B8259';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#0D9668';
              }}
            >
              {completeCandidateScoreMutation.isPending ? (
                <>
                  <Loader2
                    className="h-[14px] w-[14px] animate-spin"
                    strokeWidth={2}
                  />
                  Calculating...
                </>
              ) : (
                <>
                  <Calculator className="h-[14px] w-[14px]" strokeWidth={2} />
                  Calculate Score
                </>
              )}
            </button>
          )}
        </div>

        {/* Score Stats Grid (readonly only) */}
        {isReadonly && (
          <div className="grid grid-cols-4 gap-[14px] mb-4">
            {[
              {
                label: 'Final Score',
                value: scoresData?.data?.items?.score_data?.final_score,
                format: (v) => Number(v).toFixed(2),
              },
              {
                label: 'Tests Score',
                value: scoresData?.data?.items?.score_data?.test_score,
                format: (v) => Number(v).toFixed(2),
              },
              {
                label: 'Resume Score',
                value: scoresData?.data?.items?.score_data?.resume_score,
                format: (v) => Number(v).toFixed(2),
              },
            ].map(({ label, value, format }) => (
              <div
                key={label}
                className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] px-5 py-[18px]"
              >
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2">
                  {label}
                </div>
                <div className="text-[26px] font-bold text-foreground-strong tracking-[-0.03em] leading-none">
                  {value != null ? format(value) : '—'}
                </div>
              </div>
            ))}
            <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] px-5 py-[18px]">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-2">
                Assessment
              </div>
              <div
                className="text-[26px] font-bold tracking-[-0.03em] leading-none"
                style={{
                  color: getPassedColor(
                    scoresData?.data?.items?.score_data?.passed
                  ),
                }}
              >
                {getPassedLabel(scoresData?.data?.items?.score_data?.passed)}
              </div>
            </div>
          </div>
        )}

        {/* Test Groups Section Card */}
        <div className="bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)] overflow-hidden">
          <div className="px-5 py-4 border-b border-border">
            <span
              className="text-[14px] text-foreground-strong tracking-[-0.01em]"
              style={{ fontWeight: 650 }}
            >
              Test Groups &amp; Scores
            </span>
          </div>

          {isScoresLoading && (
            <div className="flex flex-col items-center justify-center py-[60px] gap-3 text-muted-foreground text-[13px]">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              Loading test groups...
            </div>
          )}
          {!isScoresLoading && !scoresData?.data?.items?.groups?.length && (
            <div className="text-center py-10 text-muted-foreground text-[13px]">
              No test groups available
            </div>
          )}
          {!isScoresLoading && scoresData?.data?.items?.groups?.length > 0 && (
            <div>
              {scoresData.data.items.groups
                .sort((a, b) => a.order_number - b.order_number)
                .map((group) => {
                  const isExpanded = expandedGroups.includes(group.id);
                  const isPending = group.status === 'PENDING';
                  const canEdit =
                    !isReadonly &&
                    isPending &&
                    (group.manual_update || group.manual_entry);
                  const hasWarnings = group.warnings?.length > 0;

                  return (
                    <div
                      key={group.id}
                      className="border-b border-border last:border-b-0"
                    >
                      {/* Group header row */}
                      <div className="flex items-center transition-colors duration-[120ms] hover:bg-card">
                        {/* Clickable trigger (left) */}
                        <button
                          type="button"
                          className="flex-1 flex items-center gap-[10px] pl-5 pr-2 py-[14px] text-left bg-transparent border-0 cursor-pointer min-w-0 overflow-hidden"
                          onClick={() => toggleGroup(group.id)}
                        >
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-[11.5px] flex-shrink-0"
                            style={{
                              background: 'var(--brand-bg-subtle)',
                              color: 'var(--primary)',
                              fontWeight: 700,
                            }}
                          >
                            {group.order_number}
                          </div>
                          <div className="min-w-0 overflow-hidden">
                            <div className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground-strong">
                              {group.name}
                              {hasWarnings && (
                                <TooltipProvider>
                                  <Tooltip delayDuration={0}>
                                    <TooltipTrigger asChild>
                                      <span
                                        role="button"
                                        tabIndex={-1}
                                        className="inline-flex cursor-pointer"
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => e.stopPropagation()}
                                      >
                                        <AlertCircle
                                          className="w-[15px] h-[15px] text-[#B45309]"
                                          strokeWidth={1.8}
                                        />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">
                                      <p className="font-semibold mb-1 text-xs">
                                        Warnings:
                                      </p>
                                      <ul className="list-disc list-inside space-y-1 text-xs">
                                        {group.warnings.map((w) => (
                                          <li key={w}>{w}</li>
                                        ))}
                                      </ul>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            {group.description && (
                              <div className="text-[11.5px] text-muted-foreground mt-[1px] overflow-hidden text-ellipsis whitespace-nowrap">
                                {group.description}
                              </div>
                            )}
                          </div>
                        </button>

                        {/* Actions (right) */}
                        <div className="flex items-center gap-2 flex-shrink-0 pr-5 pl-2 py-[14px]">
                          {/* Score chip */}
                          {group.score != null && (
                            <span
                              className="text-[12px] text-muted-foreground whitespace-nowrap"
                              style={{ fontWeight: 550 }}
                            >
                              Score:{' '}
                              <strong className="text-foreground-strong">
                                {Math.round(group.score)}
                              </strong>
                            </span>
                          )}

                          {/* Send / resend link */}
                          {!isReadonly &&
                            isPending &&
                            !group.manual_entry &&
                            !group.manual_update &&
                            (group.link_send_at ? (
                              <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenResendDialog(group);
                                      }}
                                      disabled={hasWarnings}
                                      className="inline-flex items-center gap-[5px] px-[10px] py-1 rounded-[6px] border border-border bg-card text-[#2168B2] text-[11.5px] cursor-pointer transition-all duration-150 hover:border-primary hover:bg-brand-bg-subtle disabled:opacity-50 disabled:cursor-not-allowed"
                                      style={{ fontWeight: 550 }}
                                    >
                                      <Send
                                        className="w-3 h-3"
                                        strokeWidth={1.8}
                                      />
                                      Link sent
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <span className="text-xs font-semibold flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {getRelativeTime(group.link_send_at)}
                                    </span>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendTestLink(group.id);
                                }}
                                disabled={
                                  sendingGroupId === group.id || hasWarnings
                                }
                                className="inline-flex items-center gap-[5px] px-3 py-[5px] rounded-[6px] border border-border bg-card text-muted-foreground text-[11.5px] cursor-pointer transition-all duration-150 hover:border-primary hover:text-primary hover:bg-brand-bg-subtle disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{ fontWeight: 550 }}
                              >
                                {sendingGroupId === group.id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Sending...
                                  </>
                                ) : (
                                  <>
                                    <Send
                                      className="w-3 h-3"
                                      strokeWidth={1.8}
                                    />
                                    Send Test Link
                                  </>
                                )}
                              </button>
                            ))}

                          {/* Status badge */}
                          {getStatusBadge(group.status)}

                          {/* Chevron */}
                          <ChevronDown
                            className={`w-4 h-4 text-muted-foreground transition-transform duration-200 cursor-pointer flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                            strokeWidth={2}
                            onClick={() => toggleGroup(group.id)}
                          />
                        </div>
                      </div>

                      {/* Group body */}
                      {isExpanded && (
                        <div className="border-t border-border">
                          {/* Table header */}
                          <div
                            className="grid gap-3 px-5 py-[10px] border-b border-border"
                            style={{
                              gridTemplateColumns: '1fr 120px 120px',
                              background: 'var(--card)',
                            }}
                          >
                            <span className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em]">
                              Test Name
                            </span>
                            <span className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-right">
                              Max Score
                            </span>
                            <span className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[0.06em] text-center">
                              Test Score
                            </span>
                          </div>

                          {/* Test rows */}
                          {group.tests?.length > 0 ? (
                            group.tests.map((test, idx) =>
                              renderTestRow(group, test, idx)
                            )
                          ) : (
                            <div className="text-[13px] text-muted-foreground text-center py-8">
                              No tests available in this group
                            </div>
                          )}

                          {/* Submit scores footer */}
                          {canEdit && group.tests?.length > 0 && (
                            <div
                              className="flex justify-end px-5 py-3 border-t border-border"
                              style={{ background: 'var(--card)' }}
                            >
                              <button
                                type="button"
                                onClick={() => handleBulkScoreSubmit(group)}
                                disabled={updateScoreMutation.isPending}
                                className="inline-flex items-center gap-1.5 px-4 py-[7px] rounded-lg border-0 text-[12.5px] font-semibold text-white cursor-pointer transition-all duration-150 disabled:opacity-55 disabled:cursor-not-allowed"
                                style={{ background: 'var(--primary)' }}
                              >
                                {updateScoreMutation.isPending ? (
                                  <>
                                    <Loader2
                                      className="h-[13px] w-[13px] animate-spin"
                                      strokeWidth={2}
                                    />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle2
                                      className="h-[13px] w-[13px]"
                                      strokeWidth={2}
                                    />
                                    Submit Scores
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Resend Link Dialog */}
      <Dialog open={resendDialogOpen} onOpenChange={setResendDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend Test Link</DialogTitle>
            <DialogDescription className="mt-4">
              {selectedGroupForResend && (
                <>
                  Link was sent{' '}
                  <span className="font-semibold">
                    {getRelativeTime(selectedGroupForResend.link_send_at)}
                  </span>{' '}
                  for{' '}
                  <span className="font-semibold">
                    {selectedGroupForResend.name}
                  </span>
                  . Do you want to resend the test link?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseResendDialog}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                selectedGroupForResend &&
                handleSendTestLink(selectedGroupForResend.id, true)
              }
              disabled={sendingGroupId === selectedGroupForResend?.id}
            >
              {sendingGroupId === selectedGroupForResend?.id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Resend Link
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpdateScoreForm;
